import { TFeatureSwitch } from "./features";
import { TRegionKey } from "../models/region-model";

type Keys<T extends readonly string[]> = T[number];

const apiEndpoints = {
  MatterManagementGraphQLUrl: "/mattermanagement/browse-graphql/graphql",
  MicrosoftAuthRedirectUrl:
    "/itops/integration/microsoft/authredirect/authentication/GetAuthorizationRequest?scope=mail",
  MicrosoftDeauthoriseUrl: "/itops/integration/microsoft/identity-callbackapi/Authentication/Deauthorize?scope=mail",
  ValidateMatterAssignUrl: "/mattermanagement/MailSync/Matter/ValidateAssign",
  MatterAssignUrl: "/mattermanagement/MailSync/Matter/Assign",
  MatterUnAssignUrl: "/mattermanagement/MailSync/Matter/UnAssign",
  FileDownloadUrls: "/mattermanagement/MailSync/File/DownloadUrls",
  ClientLog: "/itops/client-log",
  CreateFolderUrl: "/mattermanagement/MailSync/Folder/CreateFolder",
  AddMetricUrl: "/itops/metrics/api/AddMetric",
  IsSentMail: "/mattermanagement/MailSync/Api/IsSentMail",
  MailItemExtebdedProperties: "/mattermanagement/MailSync/Api/MailItemExtendedProperties",
} as const;

export type TApiEndpointKeys = keyof typeof apiEndpoints;

const API_ENDPOINT_KEYS = Object.keys(apiEndpoints) as TApiEndpointKeys[];

const CONFIG_KEYS = [
  "CognitoClientId",
  "CognitoUrl",
  "ApiUrl",
  "WebappUrl",
  "SignUpUrl",
  ...API_ENDPOINT_KEYS,
] as const;

type TConfigKeys = Keys<typeof CONFIG_KEYS>;

export type TConfig = Record<TConfigKeys, string> & { Features?: TFeatureSwitch[] };
type TBaseConfig = Omit<TConfig, TApiEndpointKeys>;

type TRegions = Record<TRegionKey, TConfig>;

const ENVIRONMENTS = ["Dev", "Staging", "Production"] as const;
export type TEnvironmentKey = Keys<typeof ENVIRONMENTS>;
type TEnvironments = Record<TEnvironmentKey, TRegions>;

const PRODUCTS = ["Smokeball"] as const;
export type TProductKey = Keys<typeof PRODUCTS>;
type TProducts = Record<TProductKey, TEnvironments>;

const makeConfig = (baseConfig: TBaseConfig): TConfig => {
  const config = { ...baseConfig } as TConfig;

  for (const k of API_ENDPOINT_KEYS) {
    config[k] = `${baseConfig.ApiUrl}${apiEndpoints[k]}`;
  }

  return config;
};

const allConfigs: TProducts = {
  Smokeball: {
    Dev: {
      AUS: makeConfig({
        CognitoClientId: "1m57p38muevmcd9env4rrdfdui",
        CognitoUrl: "https://devauth.smokeball.com.au",
        ApiUrl: "https://devappgw.smokeball.com.au",
        WebappUrl: "https://dev-app.smokeball.com.au",
        SignUpUrl: "https://www.smokeball.com.au/pricing",
        Features: [],
      }),
      USA: makeConfig({
        CognitoClientId: "6tpts909qimmvqhne15v88pbuf",
        CognitoUrl: "https://devauth.smokeball.com",
        ApiUrl: "https://devappgw.smokeball.com",
        WebappUrl: "https://dev-app.smokeball.com",
        SignUpUrl: "https://www.smokeball.com/pricing",
      }),
      GB: makeConfig({
        CognitoClientId: "49kqgtjtqv5cacrdrn6tg8m0as",
        CognitoUrl: "https://devauth.smokeball.co.uk",
        ApiUrl: "https://devappgw.smokeball.co.uk",
        WebappUrl: "https://dev-app.smokeball.co.uk",
        SignUpUrl: "https://www.smokeball.co.uk/pricing",
      }),
    },
    Staging: {
      AUS: makeConfig({
        CognitoClientId: "59teptb8b5alanequt28fmtj87",
        CognitoUrl: "https://datastaging-auth.smokeball.com.au",
        ApiUrl: "https://stagingappgw.smokeball.com.au",
        WebappUrl: "https://rc-app.smokeball.com.au",
        SignUpUrl: "https://www.smokeball.com.au/pricing",
      }),
      USA: makeConfig({
        CognitoClientId: "3ud96ekbq930ab3erfnb42i84j",
        CognitoUrl: "https://datastaging-auth.smokeball.com",
        ApiUrl: "https://stagingappgw.smokeball.com",
        WebappUrl: "https://rc-app.smokeball.com",
        SignUpUrl: "https://www.smokeball.com/pricing",
      }),
      GB: makeConfig({
        CognitoClientId: "76ah79judt0m3dcii8bicrvr3o",
        CognitoUrl: "https://datastaging-auth.smokeball.co.uk",
        ApiUrl: "https://stagingappgw.smokeball.co.uk",
        WebappUrl: "https://rc-app.smokeball.co.uk",
        SignUpUrl: "https://www.smokeball.co.uk/pricing",
      }),
    },
    Production: {
      AUS: makeConfig({
        CognitoClientId: "4tjc956h16ef36nn3clgra0m3p",
        CognitoUrl: "https://auth.smokeball.com.au",
        ApiUrl: "https://appgw.smokeball.com.au",
        WebappUrl: "https://app.smokeball.com.au",
        SignUpUrl: "https://www.smokeball.com.au/pricing",
      }),
      USA: makeConfig({
        CognitoClientId: "bj2tm9tus787e2km3o1vvsu7d",
        CognitoUrl: "https://auth.smokeball.com",
        ApiUrl: "https://appgw.smokeball.com",
        WebappUrl: "https://app.smokeball.com",
        SignUpUrl: "https://www.smokeball.com/pricing",
      }),
      GB: makeConfig({
        CognitoClientId: "4asda18vhgop8hlqqiiod3fpii",
        CognitoUrl: "https://auth.smokeball.co.uk",
        ApiUrl: "https://appgw.smokeball.co.uk",
        WebappUrl: "https://app.smokeball.co.uk",
        SignUpUrl: "https://www.smokeball.co.uk/pricing",
      }),
    },
  },
};

export const getConfig = (
  product: keyof TProducts,
  environment: keyof TEnvironments,
  region: keyof TRegions
): TConfig => ({ ...allConfigs[product][environment][region] });
